import {
    Component,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
} from '@angular/core';
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RegexInputDirective } from '../../../shared/directives/regex-input/regex-input.directive';
import { RegExPattern } from '../../../core/models/regexp-pattern/regex-pattern';
import { MatButtonModule } from '@angular/material/button';
import { OrganizationSearchResponse } from '../../../core/models/civi-crm/organization-search-response';
import { OrganizationService } from '../../../core/services/organization/organization.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { merge, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { DropdownOption } from '../../../core/models/dropdown-option';
import { DropdownService } from '../../../core/services/resources/dropdown.service';
import { ContactSearchRequest } from '../../../core/models/civi-crm/contact/contact-search/contact-search-request';
import { ContactSearchResponse } from '../../../core/models/civi-crm/contact/contact-search/contact-search-response';
import { ContactService } from '../../../core/services/contact/contact.service';
import { CiviCRMContactStatusEnum } from '../../../core/models/civi-crm/contact/civi-crm-contact-status-enum';
import { ContactLookupService } from './services/contact-lookup.service';
import { ContactSearchForm } from './models/contact-search-form';
import { FormGroupType } from '../../../core/models/form-group-type';
import { AppStorageService } from '../../../core/services/app-storage/app-storage.service';

@Component({
    selector: 'app-contact-lookup',
    templateUrl: './contact-lookup.component.html',
    styleUrl: './contact-lookup.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatCardModule,
        MatSelectModule,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        MatTooltipModule,
        MatIconModule,
        RegexInputDirective,
        MatButtonModule,
        MatAutocompleteModule,
    ],
})
export class ContactLookupComponent implements OnInit, OnDestroy {
    regExPattern = RegExPattern;

    destroy$: Subject<void> = new Subject<void>();

    contactOptions: ContactSearchResponse[] = [];
    filteredOrganizations: OrganizationSearchResponse[] = [];

    organizationName: string = '';

    contactStatus: CiviCRMContactStatusEnum | null = null;

    form: FormGroup<FormGroupType<ContactSearchForm>> = new FormGroup<
        FormGroupType<ContactSearchForm>
    >({
        firstName: new FormControl(''),
        lastName: new FormControl(''),
        organizationName: new FormControl(''),
        civiContactId: new FormControl(
            { value: null, disabled: true },
            Validators.required,
        ),
    });

    constructor(
        private readonly contactLookupService: ContactLookupService,
        private readonly organizationService: OrganizationService,
        private readonly dropdownService: DropdownService,
        private readonly contactService: ContactService,
        private readonly appStorageService: AppStorageService,
    ) {}

    ngOnInit(): void {
        this.setupFormHandlers();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Determines whether the Admin/Advisor has provided search criteria to search for a contact.
     * @returns A boolean value indicating whether the Admin/Advisor has provided search criteria to search for a contact.
     */
    allowForContactSearch(): boolean {
        const { firstName, lastName, organizationName } = this.form.value;

        const enableContactSearch = [
            firstName,
            lastName,
            organizationName,
        ].some((field) => field?.trim());

        if (enableContactSearch && this.contactOptions.length) {
            this.form.controls.civiContactId?.enable({ emitEvent: false });
        } else {
            this.form.controls.civiContactId?.disable({ emitEvent: false });
            this.clearContactOptions();
        }

        return enableContactSearch;
    }

    /**
     * Used to populate the contact dropdown options based on the search criteria provided by the Admin/Advisor.
     */
    populateContactDropdownOptions(): void {
        if (!this.allowForContactSearch()) {
            this.clearContactOptions();
            return;
        }

        this.contactService
            .searchContacts(this.form.value as ContactSearchRequest)
            .pipe(
                takeUntil(this.destroy$),
                tap((options: ContactSearchResponse[]) => {
                    this.contactOptions = options;
                }),
            )
            .subscribe();
    }

    /**
     * Begins CiviCRM contact evaluation to determine if the status is appropriate for the application process.
     */
    evaluateContactStatus(): void {
        const { civiContactId } = this.form.value;

        if (!civiContactId) {
            throw new Error('CiviCRM Contact ID is not provided.');
        }

        if (!this.contactStatus) {
            throw new Error(
                'Contact status is not available for selected CiviCRM Contact.',
            );
        }

        this.appStorageService.setContactCiviId(civiContactId);

        this.contactLookupService.evaluateContactStatus(
            civiContactId,
            this.contactStatus,
        );
    }

    /**
     * Handles clearing the selection of the organization dropdown.
     */
    clearOrganizationSearch(): void {
        this.organizationName = '';
        this.filteredOrganizations = [];
        this.form.controls.organizationName?.reset();
    }

    /**
     * Handles the updating of the organization options in the dropdown with the value provided.
     * @param value The value typed by the Admin/Advisor in the organization search field.
     */
    onOrganizationInputChange(value: string): void {
        this.organizationName = value;
        this.populateOrganizationDropdownOptions();
    }

    /**
     * Displays the billing notes for the selected contact from CiviCRM.
     * Trims extra whitespace and ensures empty billing notes return null.
     * @returns the billing notes for the selected contact or null.
     */
    billingNotesForSelectedContact(): string | null {
        const { civiContactId } = this.form.value;

        const billingNotes = this.contactOptions
            .find((contact) => contact.id === civiContactId)
            ?.billingNotes?.trim();

        return billingNotes || null;
    }

    /**
     * Converts the contact options to dropdown options for selection.
     * @returns contact dropdown options for selection.
     */
    contactDropdownOptions(): DropdownOption[] {
        return this.dropdownService.transformContactsToDropdownOptions(
            this.contactOptions,
        );
    }

    private populateOrganizationDropdownOptions(): void {
        if (!this.organizationName) {
            return;
        }

        this.organizationService
            .searchOrganizations(this.organizationName)
            .then((organizations: OrganizationSearchResponse[]) => {
                this.filteredOrganizations = organizations;
            });
    }

    private setupFormHandlers(): void {
        merge(
            this.form.controls.firstName.valueChanges,
            this.form.controls.lastName.valueChanges,
            this.form.controls.organizationName.valueChanges,
        )
            .pipe(
                takeUntil(this.destroy$),
                tap(() => {
                    this.handleContactLookupFormFieldChanges();
                }),
            )
            .subscribe();

        /**
         * Upon change for the CiviCRM contact ID form control, fetch the contact status
         * of the selected contact if there is a value present.
         */
        this.form.controls.civiContactId.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                switchMap((civiContactId: number | null) => {
                    if (!civiContactId) {
                        return of(null);
                    }
                    return this.contactService.getContactStatus(civiContactId);
                }),
                tap((status: CiviCRMContactStatusEnum | null) => {
                    this.contactStatus = status;
                }),
            )
            .subscribe();
    }

    private handleContactLookupFormFieldChanges(): void {
        const { firstName, lastName, organizationName } = this.form.controls;

        if (!firstName.value && !lastName.value && !organizationName.value) {
            this.form.controls.civiContactId?.reset();
            this.form.controls.civiContactId?.disable({ emitEvent: false });
            this.clearContactOptions();
        }
    }

    private clearContactOptions(): void {
        this.contactOptions = [];
        this.appStorageService.clearContactCiviId();
    }
}
